const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';
const DOCKER_HUB_API_BASE = 'https://hub.docker.com/v2';

class DockerHubService {
  constructor() {
    this.token = localStorage.getItem('dockerHubToken');
    this.username = localStorage.getItem('dockerHubUsername');
  }

  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/dockerhub/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        this.setAuth(data.token, username);
        return { success: true, token: data.token };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Docker Hub login error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async logout() {
    this.clearAuth();
    return { success: true };
  }

  async getRepositories(namespace = null, page = 1, pageSize = 100) {
    const targetNamespace = namespace || this.username;
    if (!targetNamespace) {
      return { success: false, error: 'No namespace specified' };
    }

    try {
      const params = new URLSearchParams({
        namespace: targetNamespace,
        page: page.toString(),
        page_size: pageSize.toString()
      });

      const response = await fetch(`${API_BASE_URL}/dockerhub/repositories?${params}`, {
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          repositories: data.repositories || [],
          count: data.count || 0,
          next: data.next,
          previous: data.previous
        };
      } else {
        return { success: false, error: data.error || 'Failed to fetch repositories' };
      }
    } catch (error) {
      console.error('Docker Hub repositories error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getRepositoryDetails(namespace, repository) {
    try {
      const response = await fetch(`${DOCKER_HUB_API_BASE}/repositories/${namespace}/${repository}/`, {
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, repository: data };
      } else {
        return { success: false, error: data.detail || 'Failed to fetch repository details' };
      }
    } catch (error) {
      console.error('Docker Hub repository details error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getRepositoryTags(namespace, repository) {
    try {
      const params = new URLSearchParams({
        namespace: namespace,
        repository: repository
      });

      const url = `${API_BASE_URL}/dockerhub/tags?${params}`;

      const response = await fetch(url, {
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, tags: data.tags || [] };
      } else {
        return { success: false, error: data.error || 'Failed to fetch tags' };
      }
    } catch (error) {
      console.error('Docker Hub tags error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async searchRepositories(query, limit = 100, page = 1) {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: limit.toString(),
        page: page.toString()
      });

      const response = await fetch(`${API_BASE_URL}/dockerhub/search?${params}`, {
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          repositories: data.repositories || [],
          count: data.count || 0,
          next: data.next,
          previous: data.previous
        };
      } else {
        return { success: false, error: data.error || 'Search failed' };
      }
    } catch (error) {
      console.error('Docker Hub search error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getUserProfile() {
    if (!this.username) {
      return { success: false, error: 'Not logged in' };
    }

    try {
      const response = await fetch(`${DOCKER_HUB_API_BASE}/users/${this.username}/`, {
        headers: this.getHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, profile: data };
      } else {
        return { success: false, error: data.detail || 'Failed to fetch profile' };
      }
    } catch (error) {
      console.error('Docker Hub profile error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  setAuth(token, username) {
    this.token = token;
    this.username = username;
    localStorage.setItem('dockerHubToken', token);
    localStorage.setItem('dockerHubUsername', username);
  }

  clearAuth() {
    this.token = null;
    this.username = null;
    localStorage.removeItem('dockerHubToken');
    localStorage.removeItem('dockerHubUsername');
  }

  isAuthenticated() {
    return !!this.token;
  }

  getUsername() {
    return this.username;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `JWT ${this.token}`;
    }

    return headers;
  }
}

const dockerHubService = new DockerHubService();
export default dockerHubService;